import { Controller } from "@hotwired/stimulus";

// Manages postulant selection inside the recrutement module:
//   - Master "select all" checkbox
//   - Per-row checkboxes
//   - Floating bulk action bar that shows the selected count
//   - Bulk accept: opens the bulk_accept modal and injects one ref_rm row per
//     selected postulant before letting the user submit
//   - Bulk refuse: confirm + plain POST to bulk_refuse endpoint
//   - Solo accept: uses the same bulk modal pre-populated with one row
export default class extends Controller {
  static targets = [
    "checkbox", "masterCheckbox", "bulkbar",
    "selectedCount", "selectedPlural", "selectedPlural2",
    "bulkRows", "bulkEmpty", "bulkSubmit",
    "bulkSubmitCount", "bulkSubmitPlural",
    "bulkForm",
  ];

  static values = {
    bulkAcceptModalId: String,
    bulkRefuseUrl: String,
  };

  // ---- Selection bookkeeping -----------------------------------------

  toggleAll(event) {
    const on = event.currentTarget.checked;
    this.checkboxTargets.forEach((cb) => { cb.checked = on; });
    this.updateSelection();
  }

  updateSelection() {
    const count = this._selectedIds().length;
    if (this.hasBulkbarTarget) this.bulkbarTarget.hidden = count === 0;
    if (this.hasSelectedCountTarget) this.selectedCountTarget.textContent = count;
    const plural = count > 1 ? "s" : "";
    if (this.hasSelectedPluralTarget) this.selectedPluralTarget.textContent = plural;
    if (this.hasSelectedPlural2Target) this.selectedPlural2Target.textContent = plural;

    // Sync the master checkbox indeterminate state.
    if (this.hasMasterCheckboxTarget) {
      const total = this.checkboxTargets.length;
      this.masterCheckboxTarget.checked = total > 0 && count === total;
      this.masterCheckboxTarget.indeterminate = count > 0 && count < total;
    }
  }

  clearSelection() {
    this.checkboxTargets.forEach((cb) => { cb.checked = false; });
    this.updateSelection();
  }

  // ---- Bulk accept (opens the dedicated modal) -----------------------

  openBulkAccept() {
    const ids = this._selectedIds();
    if (ids.length === 0) return;
    this._populateBulkRows(ids);
    this._dispatchOpenModal(this.bulkAcceptModalIdValue);
  }

  // ---- Solo accept (one postulant via the same modal) ----------------

  openSoloAccept(event) {
    const id = event.currentTarget.dataset.postulantId;
    const name = event.currentTarget.dataset.postulantName;
    if (!id) return;
    this._populateBulkRows([{ id, name }]);
    this._dispatchOpenModal(this.bulkAcceptModalIdValue);
  }

  // ---- Bulk refuse ----------------------------------------------------

  openBulkRefuse() {
    const ids = this._selectedIds();
    if (ids.length === 0) return;
    if (!confirm(`Refuser ${ids.length} postulant${ids.length > 1 ? "s" : ""} ?`)) return;

    const form = document.createElement("form");
    form.method = "POST";
    form.action = this.bulkRefuseUrlValue;
    form.style.display = "none";

    const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
    if (csrf) {
      const t = document.createElement("input");
      t.type = "hidden"; t.name = "authenticity_token"; t.value = csrf;
      form.appendChild(t);
    }
    ids.forEach((rawId) => {
      const id = typeof rawId === "object" ? rawId.id : rawId;
      const i = document.createElement("input");
      i.type = "hidden"; i.name = "postulant_ids[]"; i.value = id;
      form.appendChild(i);
    });
    document.body.appendChild(form);
    form.submit();
  }

  // ---- Internals ------------------------------------------------------

  _selectedIds() {
    return this.checkboxTargets
      .filter((cb) => cb.checked)
      .map((cb) => {
        const li = cb.closest("[data-postulant-id]");
        return {
          id: cb.value,
          name: li?.dataset.postulantName || "",
        };
      });
  }

  // Renders one row per selected postulant inside the bulk modal.
  // Each row produces:
  //   - <input type="hidden" name="assignments[][postulant_id]" value="…">
  //   - <input type="text"   name="assignments[][ref_rm]" required>
  // Rails' nested-array convention (`name[]`) groups them by index.
  _populateBulkRows(items) {
    const list = items.map((it) => (typeof it === "object" ? it : { id: it, name: "" }));
    if (!this.hasBulkRowsTarget) return;

    if (list.length === 0) {
      this.bulkRowsTarget.innerHTML = "";
      this.bulkEmptyTarget.hidden = false;
      this._setSubmitState(0);
      return;
    }
    if (this.hasBulkEmptyTarget) this.bulkEmptyTarget.hidden = true;

    this.bulkRowsTarget.innerHTML = list.map((p, idx) => `
      <div class="esm_bulk-accept__row">
        <input type="hidden" name="assignments[${idx}][postulant_id]" value="${p.id}" />
        <div class="esm_bulk-accept__row-name">
          <span class="esm_bulk-accept__row-num">${idx + 1}</span>
          <span>${this._escape(p.name || `Postulant #${p.id}`)}</span>
        </div>
        <div class="esm_bulk-accept__row-input">
          <input type="text"
                 name="assignments[${idx}][ref_rm]"
                 class="esm_form__input"
                 placeholder="Réf. RM (ex : RM-2024-018)"
                 required />
        </div>
      </div>
    `).join("");

    this._setSubmitState(list.length);
  }

  _setSubmitState(count) {
    if (this.hasBulkSubmitTarget) this.bulkSubmitTarget.disabled = count === 0;
    if (this.hasBulkSubmitCountTarget) this.bulkSubmitCountTarget.textContent = count;
    if (this.hasBulkSubmitPluralTarget) this.bulkSubmitPluralTarget.textContent = count > 1 ? "s" : "";
  }

  _dispatchOpenModal(modalId) {
    // Find the modal in the DOM and toggle its open state directly. Avoids
    // the Stimulus action wiring (which doesn't fire on synthetic elements
    // appended just-in-time) by reusing the same CSS classes the
    // cockpit-modal controller sets.
    const modal = document.getElementById(modalId);
    if (!modal) return;
    modal.classList.add("esm_modal--open");
    modal.removeAttribute("hidden");
    document.body.classList.add("esm_modal-lock");
    setTimeout(() => {
      const first = modal.querySelector("input:not([type='hidden']):not([disabled])");
      first?.focus();
    }, 50);
  }

  _escape(s) {
    const div = document.createElement("div");
    div.textContent = s;
    return div.innerHTML;
  }
}
